import React, { useState, useMemo, useEffect, useRef } from 'react';
import { API_BASE } from '../services/api';
import { AlertTriangle, Clock, RotateCcw, Share2, CheckCheck, Save, X } from 'lucide-react';
import { buildShareUrl, readShareStateFromUrl, clearShareFromUrl, copyToClipboard, ShareState } from './utils/shareUtils';
import { EstateAssets, EstateProfile } from '../types';
import { syncResidenceContext } from '../server/services/rnrbResidenceFacts';
import { DebugPanel } from './DebugPanel';
import { useAuth } from './auth/AuthContext';
import { Button, StepRail, SectionHeader } from './ui';

// Steps
import { WelcomeStep } from './steps/WelcomeStep';
import { AssetDiscoveryStep } from './steps/assets/AssetDiscoveryStep';
import { PropertyStep } from './steps/assets/PropertyStep';
import { BankAccountsStep } from './steps/assets/BankAccountsStep';
import { InvestmentsStep } from './steps/assets/InvestmentsStep';
import { PensionsStep } from './steps/assets/PensionsStep';
import { GiftsStep } from './steps/assets/GiftsStep';
import { DebtsStep } from './steps/assets/DebtsStep';
import { BprStep } from './steps/assets/BprStep';
import { AprStep } from './steps/assets/AprStep';
import { BasicsStep } from './steps/BasicsStep';
import { BeneficiariesStep } from './steps/BeneficiariesStep';
import { ComplexityStep } from './steps/ComplexityStep';
import { ReviewStep } from './steps/ReviewStep';
import { SummaryStep } from './steps/SummaryStep';

export type WizardData = {
    EstateProfile: EstateProfile;
    EstateAssets: EstateAssets;
};

/**
 * Default localStorage key used by the consumer save/resume flow. The
 * consumer never has more than one in-flight audit at a time so a single
 * fixed key is safe — and changing it would orphan existing draft audits
 * stored in users' browsers. Pro callers pass an explicit per-case
 * `storageKey` prop so cases on the same browser cannot collide.
 */
const DEFAULT_AUTOSAVE_KEY = 'probate_wizard_autosave_v1';
const ESTATE_ID_KEY = 'probate_current_estate_id';

/**
 * Build a per-estate autosave key. Pro callers use this so case A and case
 * B on the same browser write to different localStorage entries.
 */
export function autosaveKeyForEstate(estateId: string): string {
    return `${DEFAULT_AUTOSAVE_KEY}:estate:${estateId}`;
}

type SavedSession = {
    data: WizardData;
    selectedCategories: string[];
    currentStep: number;
    savedAt: string;
};

function formatSavedAt(iso: string): string {
    try {
        return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
        return '';
    }
}

interface ProbateWizardProps {
    /**
     * When true on the first mount, skip the "Resume session" prompt and jump
     * straight to the last saved step. Set by the app after a Stripe unlock so
     * the user lands on the unlocked report, not the welcome screen. Passed as
     * a prop (not read from localStorage) so React StrictMode's dev-only
     * double-mount cannot consume the flag before the visible render runs.
     */
    autoResume?: boolean;
    /**
     * Called when the user clicks Save while logged out — the host app should
     * open the auth modal (typically on the register tab) so the user can
     * create an account and have their in-progress audit persisted.
     */
    onRequestAuth?: (message?: string) => void;
    /**
     * Per-mount localStorage key for autosave/resume. Defaults to the
     * consumer key (one audit per browser). Pro callers must pass a
     * per-case key so audits on the same browser cannot overwrite each
     * other. Use `autosaveKeyForEstate(estateId)` to generate a stable
     * scoped key.
     */
    storageKey?: string;
}

export const ProbateWizard: React.FC<ProbateWizardProps> = ({
    autoResume = false,
    onRequestAuth,
    storageKey = DEFAULT_AUTOSAVE_KEY,
}) => {
    // Capture the storage key into a ref so the autosave/load effects (which
    // run with their own dependency lists) always see the same key the wizard
    // mounted with. Changing the prop after mount is not supported — the host
    // should remount the wizard via React `key` instead, the way App.tsx
    // already does for consumer fresh-start.
    const AUTOSAVE_KEY = storageKey;
    const { user } = useAuth();
    const sessionNonce = React.useRef(Math.random().toString(36).slice(2)).current;
    const [showDebug, setShowDebug] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [data, setData] = useState<WizardData>({
        EstateProfile: { deceased: { date_of_death: new Date().toISOString().split('T')[0] } },
        EstateAssets: { properties: [], bank_accounts: [], investments: [], pensions: [], debts: [] }
    });

    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

    const [visitedStepIds, setVisitedStepIds] = useState<Set<string>>(() => new Set(['welcome']));
    const [stepsNeedingReview, setStepsNeedingReview] = useState<Set<string>>(() => new Set());
    const prevStepRef = useRef(0);

    const [savedSession, setSavedSession] = useState<SavedSession | null>(null);
    const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const initialLoadDone = useRef(false);

    const [estateId, setEstateId] = useState<string | null>(null);
    const estateIdRef = useRef<string | null>(null);
    useEffect(() => { estateIdRef.current = estateId; }, [estateId]);

    const dataRef = useRef(data);
    const selectedCategoriesRef = useRef(selectedCategories);
    const currentStepRef = useRef(currentStep);
    useEffect(() => { dataRef.current = data; }, [data]);
    useEffect(() => { selectedCategoriesRef.current = selectedCategories; }, [selectedCategories]);
    useEffect(() => { currentStepRef.current = currentStep; }, [currentStep]);

    function hasRealData(d: WizardData): boolean {
        const a = d.EstateAssets;
        const p = d.EstateProfile;
        if ((a.properties?.length ?? 0) > 0) return true;
        if ((a.bank_accounts?.length ?? 0) > 0) return true;
        if ((a.investments?.length ?? 0) > 0) return true;
        if ((a.pensions?.length ?? 0) > 0) return true;
        if ((a.debts?.length ?? 0) > 0) return true;
        if ((a.lifetime_gifts?.length ?? 0) > 0) return true;
        if (p.deceased?.maritalStatus) return true;
        if (p.deceased?.domicile) return true;
        if (p.case_label) return true;
        if (p.family?.assets_passed_to_spouse) return true;
        if (p.iht_details?.predeceased_spouse?.exists !== undefined && p.iht_details?.predeceased_spouse?.exists !== null) return true;
        return false;
    }

    useEffect(() => {
        const applySession = (session: SavedSession) => {
            if (autoResume) {
                setData(session.data);
                setSelectedCategories(session.selectedCategories);
                setCurrentStep(session.currentStep);
                initialLoadDone.current = true;
            } else {
                setSavedSession(session);
            }
        };

        const storedId = localStorage.getItem(ESTATE_ID_KEY);
        if (storedId) {
            setEstateId(storedId);
            estateIdRef.current = storedId;
            fetch(`/api/estates/${storedId}`, { credentials: 'include' })
                .then(r => r.ok ? r.json() : null)
                .then((record: any) => {
                    if (record?.wizard_data_json && hasRealData(record.wizard_data_json)) {
                        applySession({
                            data: record.wizard_data_json,
                            selectedCategories: record.selected_categories_json ?? [],
                            currentStep: Math.max(1, record.current_step ?? 1),
                            savedAt: record.updated_at,
                        });
                        initialLoadDone.current = true;
                    } else {
                        loadFromLocalStorage();
                    }
                })
                .catch(() => {
                    loadFromLocalStorage();
                });
            return;
        }
        loadFromLocalStorage();

        function loadFromLocalStorage() {
            try {
                const raw = localStorage.getItem(AUTOSAVE_KEY);
                if (raw) {
                    const parsed = JSON.parse(raw) as SavedSession;
                    if (parsed?.data && parsed?.savedAt && (parsed.currentStep ?? 0) > 0) {
                        applySession(parsed);
                    }
                }
            } catch { /* corrupt data — ignore */ }
            initialLoadDone.current = true;
        }
    }, []);

    // Create DB record when authenticated user moves past welcome.
    useEffect(() => {
        if (!user) return;
        if (currentStep === 0 || estateIdRef.current) return;
        const timer = setTimeout(() => {
            const latestData = dataRef.current;
            const latestCategories = selectedCategoriesRef.current;
            const latestStep = currentStepRef.current;
            fetch(`${API_BASE}/api/estates`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ wizard_data: latestData, selected_categories: latestCategories, current_step: latestStep }),
            })
                .then(r => r.ok ? r.json() : null)
                .then((record: any) => {
                    if (record?.id) {
                        setEstateId(record.id);
                        estateIdRef.current = record.id;
                        localStorage.setItem(ESTATE_ID_KEY, record.id);
                    }
                })
                .catch(() => { /* silent fail */ });
        }, 300);
        return () => clearTimeout(timer);
    }, [currentStep, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-save (500ms debounce)
    useEffect(() => {
        if (currentStep === 0) return;
        if (!initialLoadDone.current) return;
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => {
            try {
                const session: SavedSession = { data, selectedCategories, currentStep, savedAt: new Date().toISOString() };
                localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(session));
            } catch { /* storage quota */ }
            if (estateIdRef.current) {
                fetch(`/api/estates/${estateIdRef.current}`, {
                    method: 'PATCH',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ wizard_data: data, selected_categories: selectedCategories, current_step: currentStep }),
                }).catch((err) => { console.warn('[autosave] DB patch failed:', err.message); });
            }
        }, 500);
        return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
    }, [data, selectedCategories, currentStep]);

    const handleResume = () => {
        if (!savedSession) return;
        setData(savedSession.data);
        setSelectedCategories(savedSession.selectedCategories);
        setCurrentStep(savedSession.currentStep);
        setSavedSession(null);
    };

    const handleStartFresh = () => {
        localStorage.removeItem(AUTOSAVE_KEY);
        localStorage.removeItem(ESTATE_ID_KEY);
        setEstateId(null);
        estateIdRef.current = null;
        setSavedSession(null);
        initialLoadDone.current = true;
    };

    // Shareable link
    const [sharedState, setSharedState] = useState<ShareState | null>(null);
    const [shareCopied, setShareCopied] = useState(false);

    useEffect(() => {
        const found = readShareStateFromUrl();
        if (found) {
            setSharedState(found);
            clearShareFromUrl();
        }
    }, []);

    const handleLoadShared = () => {
        if (!sharedState) return;
        setData(sharedState.data);
        setSelectedCategories(sharedState.selectedCategories);
        setCurrentStep(sharedState.currentStep);
        setSharedState(null);
        initialLoadDone.current = true;
    };

    const handleShare = async () => {
        const url = buildShareUrl({ data, selectedCategories, currentStep });
        const ok = await copyToClipboard(url);
        if (ok) {
            setShareCopied(true);
            setTimeout(() => setShareCopied(false), 2500);
        }
    };

    // Manual save
    const [saveConfirmed, setSaveConfirmed] = useState(false);
    const [showCaseNameModal, setShowCaseNameModal] = useState(false);
    const [caseNameInput, setCaseNameInput] = useState('');
    // True when an anonymous user clicked Save and we opened the auth modal —
    // once they sign in we resume the save automatically and show the banner.
    const [pendingSaveAfterAuth, setPendingSaveAfterAuth] = useState(false);
    const banDismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const showSaveBanner = () => {
        setSaveConfirmed(true);
        if (banDismissTimer.current) clearTimeout(banDismissTimer.current);
        banDismissTimer.current = setTimeout(() => setSaveConfirmed(false), 4500);
    };

    const performSave = (dataToSave: WizardData) => {
        try {
            const session: SavedSession = { data: dataToSave, selectedCategories, currentStep, savedAt: new Date().toISOString() };
            localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(session));
        } catch { /* ignore */ }
        if (estateIdRef.current) {
            fetch(`/api/estates/${estateIdRef.current}`, {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ wizard_data: dataToSave, selected_categories: selectedCategories, current_step: currentStep }),
            }).catch(() => { /* silent */ });
        }
        showSaveBanner();
    };

    const handleManualSave = () => {
        // Anonymous user — defer the save until they sign in. The existing
        // localStorage autosave already preserves their wizard state across
        // the auth round-trip, and the create-on-step useEffect will spin up
        // the estate row once auth resolves.
        if (!user) {
            setPendingSaveAfterAuth(true);
            onRequestAuth?.('Create an account to save your audit and return to it later.');
            return;
        }
        const hasName = data.EstateProfile?.case_label?.trim() ||
            data.EstateProfile?.deceased?.firstName?.trim();
        if (!hasName) {
            setCaseNameInput('');
            setShowCaseNameModal(true);
            return;
        }
        performSave(data);
    };

    // After the user signs in following a save click, complete the save once
    // the estate row exists. The existing useEffect at line ~162 creates the
    // row within ~300ms of authentication; we wait for estateId, then PATCH
    // and surface the confirmation banner.
    useEffect(() => {
        if (!pendingSaveAfterAuth) return;
        if (!user || !estateId) return;
        setPendingSaveAfterAuth(false);
        performSave(dataRef.current);
    }, [pendingSaveAfterAuth, user, estateId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCaseNameConfirm = () => {
        const label = caseNameInput.trim();
        const updatedData: WizardData = {
            ...data,
            EstateProfile: { ...data.EstateProfile, case_label: label || undefined },
        };
        setData(updatedData);
        setShowCaseNameModal(false);
        performSave(updatedData);
    };

    // Every step edit passes through here. For a residence-facts version-2 estate this keeps the derived state in step
    // (a stale nomination is cleared) so it is persisted with the estate. The is_main_residence mirror is retired (Slice 6).
    // It never writes residence_facts_version: only the Properties step's residence answer does (applyResidenceAnswer).
    // Loading a saved estate uses setData directly and changes nothing.
    const updateData = (partial: Partial<WizardData>) => {
        setData(prev => syncResidenceContext({ ...prev, ...partial }));
    };

    const updateAssets = (assets: EstateAssets) => {
        updateData({ EstateAssets: assets });
    };

    // Dynamic step list — unchanged from legacy.
    const steps = useMemo(() => {
        const base = [{ id: 'welcome', title: 'Welcome', component: <WelcomeStep onNext={() => setCurrentStep(1)} /> }];

        base.push({
            id: 'basics',
            title: 'Basics',
            component: <BasicsStep data={data.EstateProfile} onChange={p => updateData({ EstateProfile: p })} />
        });

        base.push({
            id: 'discovery',
            title: 'Discovery',
            component: <AssetDiscoveryStep
                selectedCategories={selectedCategories}
                onChange={setSelectedCategories}
                assets={data.EstateAssets}
                onAssetsChange={updateAssets}
            />
        });

        if (selectedCategories.includes('property')) {
            base.push({
                id: 'property',
                title: 'Properties',
                component: <PropertyStep
                    data={data.EstateAssets}
                    onChange={updateAssets}
                    profile={data.EstateProfile}
                    onProfileChange={(p) => updateData({ EstateProfile: p })}
                />
            });
        }
        if (selectedCategories.includes('banks')) {
            base.push({ id: 'banks', title: 'Accounts', component: <BankAccountsStep data={data.EstateAssets} onChange={updateAssets} /> });
        }
        if (selectedCategories.includes('investments')) {
            base.push({ id: 'investments', title: 'Investments', component: <InvestmentsStep data={data.EstateAssets} onChange={updateAssets} /> });
        }
        if (selectedCategories.includes('pensions')) {
            base.push({ id: 'pensions', title: 'Pensions', component: <PensionsStep data={data.EstateAssets} onChange={updateAssets} /> });
        }
        if (selectedCategories.includes('gifts')) {
            base.push({ id: 'gifts', title: 'Gifts', component: <GiftsStep data={data.EstateAssets} onChange={updateAssets} onNext={() => setCurrentStep(p => p + 1)} /> });
        }
        if (selectedCategories.includes('debts')) {
            base.push({ id: 'debts', title: 'Debts', component: <DebtsStep data={data.EstateAssets} onChange={updateAssets} /> });
        }
        if (selectedCategories.includes('bpr')) {
            base.push({ id: 'bpr', title: 'Business (BPR)', component: <BprStep data={data.EstateAssets} onChange={updateAssets} /> });
        }
        if (selectedCategories.includes('apr')) {
            base.push({ id: 'apr', title: 'Agriculture (APR)', component: <AprStep data={data.EstateAssets} onChange={updateAssets} /> });
        }

        base.push({
            id: 'beneficiaries',
            title: 'Beneficiaries',
            component: <BeneficiariesStep
                data={data.EstateProfile}
                onChange={p => updateData({ EstateProfile: p })}
                assets={data.EstateAssets}
                goToStep={(stepId) => {
                    const idx = base.findIndex(s => s.id === stepId);
                    if (idx !== -1) {
                        setCurrentStep(idx);
                        return true;
                    }
                    return false;
                }}
            />
        });
        base.push({
            id: 'complexity',
            title: 'Complexity',
            component: <ComplexityStep
                assets={data.EstateAssets}
                onChange={updateAssets}
                profile={data.EstateProfile}
                onProfileChange={(p) => updateData({ EstateProfile: p })}
            />
        });

        base.push({
            id: 'review',
            title: 'Review',
            component: <SummaryStep
                data={data}
                updateAssets={updateAssets}
                updateProfile={(p) => updateData({ EstateProfile: p })}
                goToStep={(stepId) => {
                    const idx = base.findIndex(s => s.id === stepId);
                    if (idx !== -1) {
                        setCurrentStep(idx);
                        return true;
                    }
                    return false;
                }}
            />
        });

        base.push({
            id: 'audit',
            title: 'Run Audit',
            component: <ReviewStep
                data={data}
                updateAssets={updateAssets}
                estateId={estateId}
                autoRun={autoResume}
                goToStep={(stepId) => {
                    const idx = base.findIndex(s => s.id === stepId);
                    if (idx !== -1) {
                        setCurrentStep(idx);
                        return true;
                    }
                    return false;
                }}
            />
        });

        return base;
    }, [selectedCategories, data, autoResume]);

    const next = () => setCurrentStep(p => Math.min(steps.length - 1, p + 1));
    const back = () => setCurrentStep(p => Math.max(0, p - 1));

    const activeStep = steps[currentStep];
    const isWelcome = currentStep === 0;
    const isAudit = activeStep?.id === 'audit';
    const isLastStep = currentStep === steps.length - 1;

    // On step change, scroll back to the top so the new screen starts at its first section.
    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
    }, [currentStep]);

    // Visited tracking — every step at or below currentStep counts as visited.
    useEffect(() => {
        setVisitedStepIds(prev => {
            let changed = false;
            const nextSet = new Set(prev);
            for (let i = 0; i <= currentStep && i < steps.length; i++) {
                const id = steps[i].id;
                if (!nextSet.has(id)) { nextSet.add(id); changed = true; }
            }
            return changed ? nextSet : prev;
        });
    }, [currentStep, steps]);

    // Backward navigation flags later steps for review.
    useEffect(() => {
        const prev = prevStepRef.current;
        if (currentStep < prev) {
            setStepsNeedingReview(reviewSet => {
                const nextSet = new Set(reviewSet);
                let changed = false;
                for (let i = currentStep + 1; i < steps.length; i++) {
                    const id = steps[i].id;
                    if (visitedStepIds.has(id) && !nextSet.has(id)) {
                        nextSet.add(id);
                        changed = true;
                    }
                }
                return changed ? nextSet : reviewSet;
            });
        } else if (currentStep > prev) {
            const id = steps[currentStep]?.id;
            if (id) {
                setStepsNeedingReview(reviewSet => {
                    if (!reviewSet.has(id)) return reviewSet;
                    const nextSet = new Set(reviewSet);
                    nextSet.delete(id);
                    return nextSet;
                });
            }
        }
        prevStepRef.current = currentStep;
    }, [currentStep, steps, visitedStepIds]);

    // Validation gate — Continue disabled on Basics until required fields entered.
    const continueDisabled =
        activeStep?.id === 'basics' && (
            !data.EstateProfile.deceased?.date_of_death ||
            !data.EstateProfile.deceased?.domicile ||
            !data.EstateProfile.deceased?.maritalStatus
        );

    // Steps shown in StepRail (welcome hidden).
    const railSteps = useMemo(
        () => steps.slice(1).map(s => ({ key: s.id, label: s.title })),
        [steps]
    );
    const railIndex = Math.max(0, currentStep - 1);
    const railCompleted = useMemo(() => {
        const out: number[] = [];
        for (let i = 1; i < steps.length; i++) {
            if (i < currentStep && visitedStepIds.has(steps[i].id)) out.push(i - 1);
        }
        return out;
    }, [currentStep, steps, visitedStepIds]);

    const handleStepClick = (railIdx: number) => {
        const targetIdx = railIdx + 1; // +1 because welcome is index 0 and not in rail
        if (targetIdx >= steps.length) return;
        const id = steps[targetIdx].id;
        if (visitedStepIds.has(id) || targetIdx === currentStep) {
            setCurrentStep(targetIdx);
        }
    };

    const dismissSaveBanner = () => {
        setSaveConfirmed(false);
        if (banDismissTimer.current) clearTimeout(banDismissTimer.current);
    };

    // Inline banner — rendered in the page flow (not fixed-position) so it
    // can never be hidden behind the sticky AppTopBar regardless of
    // z-index/stacking-context behaviour. Sits directly under the wizard's
    // own header / above the main content.
    const saveBanner = saveConfirmed ? (
        <div
            role="status"
            aria-live="polite"
            className="border-b border-rule bg-accent-soft"
        >
            <div className="max-w-[960px] mx-auto px-6 sm:px-8 py-3 flex items-start gap-3">
                <CheckCheck className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-ink font-medium">Your audit has been saved.</div>
                    <div className="text-[12px] text-ink3 mt-0.5">You can return to it from this account.</div>
                </div>
                <button
                    onClick={dismissSaveBanner}
                    aria-label="Dismiss"
                    className="text-ink3 hover:text-ink shrink-0"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    ) : null;

    /* ─────────────────────── Welcome screen ─────────────────────── */
    if (isWelcome) {
        return (
            <div className="min-h-screen bg-paper">
                {saveBanner}
                <main className="max-w-[720px] mx-auto px-6 sm:px-8 pt-12 pb-20">
                    {sharedState && (
                        <div className="mb-8 border border-rule bg-white p-5 flex items-start gap-4">
                            <Share2 className="w-4 h-4 text-accent shrink-0 mt-1" />
                            <div className="flex-1 min-w-0">
                                <div className="text-[13px] text-ink font-medium">A case has been shared with you</div>
                                <div className="text-[12px] text-ink3 mt-1">
                                    Shared {new Date(sharedState.sharedAt).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
                                </div>
                                <div className="flex gap-2 mt-4">
                                    <Button variant="primary" onClick={handleLoadShared}>Load shared case</Button>
                                    <Button variant="ghost" onClick={() => setSharedState(null)}>Dismiss</Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {savedSession && (
                        <div className="mb-8 border border-rule bg-white p-5 flex items-start gap-4">
                            <Clock className="w-4 h-4 text-accent shrink-0 mt-1" />
                            <div className="flex-1 min-w-0">
                                <div className="text-[13px] text-ink font-medium">You have an unfinished session</div>
                                <div className="text-[12px] text-ink3 mt-1">
                                    Last saved {formatSavedAt(savedSession.savedAt)}
                                </div>
                                <div className="flex gap-2 mt-4">
                                    <Button variant="primary" onClick={handleResume}>Resume session</Button>
                                    <Button variant="ghost" icon={<RotateCcw className="w-3 h-3" />} onClick={handleStartFresh}>Start fresh</Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeStep?.component}
                </main>

                {/* Debug button — always available */}
                <button
                    onClick={() => setShowDebug(true)}
                    title="Open Debug Investigation Mode"
                    className="fixed top-4 right-4 z-50 inline-flex items-center gap-2 text-[11px] text-ink3 hover:text-ink border border-rule bg-white px-3 py-1.5 transition-colors"
                >
                    <AlertTriangle className="w-3.5 h-3.5" /> Debug
                </button>

                <DebugPanel
                    isOpen={showDebug}
                    onClose={() => setShowDebug(false)}
                    result={null}
                    sessionNonce={sessionNonce}
                    componentName={activeStep.component.type?.name || activeStep.id}
                    goToStep={(stepId) => {
                        const idx = steps.findIndex(s => s.id === stepId);
                        if (idx !== -1) {
                            setCurrentStep(idx);
                            return true;
                        }
                        return false;
                    }}
                />
            </div>
        );
    }

    /* ─────────────────────── Audit / report screen ───────────────────────
       Chrome (header + step rail) is rendered with the `no-print` class so
       PDF/print export still gets a clean report view. The header gives
       the user a "Save this audit" action on the results page, and the
       StepRail lets them jump back to edit earlier sections — important
       because this is where most users land after completing the wizard. */
    if (isAudit) {
        return (
            <div className="min-h-screen bg-paper">
                <header className="border-b border-rule bg-white no-print">
                    <div className="max-w-[960px] mx-auto px-6 sm:px-8 py-5 flex items-baseline justify-between gap-4">
                        <div>
                            <div className="font-serif text-[18px] text-ink">
                                Estate<span className="italic">clarity</span>
                            </div>
                            <div className="text-[10px] tracking-eyebrow uppercase text-ink3 mt-0.5">
                                Probate &amp; IHT review — results
                            </div>
                        </div>
                        <div className="flex items-center gap-4 text-[12px]">
                            <button
                                onClick={handleManualSave}
                                className="inline-flex items-center gap-1.5 text-ink3 hover:text-ink"
                                title="Save your progress"
                            >
                                <Save className="w-3.5 h-3.5" /> Save this audit
                            </button>
                            <button
                                onClick={handleShare}
                                className="inline-flex items-center gap-1.5 text-ink3 hover:text-ink"
                                title="Copy a shareable link to this case"
                            >
                                {shareCopied
                                    ? <><CheckCheck className="w-3.5 h-3.5 text-accent" /><span className="text-accent">Link copied</span></>
                                    : <><Share2 className="w-3.5 h-3.5" /> Share</>
                                }
                            </button>
                        </div>
                    </div>
                </header>

                {saveBanner}

                <div className="max-w-[960px] mx-auto px-6 sm:px-8 pt-8 no-print">
                    <StepRail
                        steps={railSteps}
                        currentIndex={railIndex}
                        completedIndices={railCompleted}
                        onStepClick={handleStepClick}
                    />
                </div>

                {activeStep?.component}

                <button
                    onClick={() => setShowDebug(true)}
                    title="Open Debug Investigation Mode"
                    className="fixed top-4 right-4 z-50 inline-flex items-center gap-2 text-[11px] text-ink3 hover:text-ink border border-rule bg-white px-3 py-1.5 transition-colors no-print"
                >
                    <AlertTriangle className="w-3.5 h-3.5" /> Debug
                </button>

                {showCaseNameModal && (
                    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 no-print">
                        <div className="bg-white border border-rule shadow-xl w-full max-w-sm p-6">
                            <h2 className="font-serif text-[20px] text-ink mb-1">Name this case</h2>
                            <p className="text-[12px] text-ink3 mb-4 leading-relaxed">
                                Give this case a name so you can find it again. Leave blank to save without one.
                            </p>
                            <input
                                type="text"
                                autoFocus
                                className="w-full border border-rule bg-white px-3 py-2.5 text-[13px] text-ink focus:border-accent focus:outline-none mb-5"
                                placeholder="e.g. Holloway estate"
                                value={caseNameInput}
                                onChange={e => setCaseNameInput(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') handleCaseNameConfirm();
                                    if (e.key === 'Escape') setShowCaseNameModal(false);
                                }}
                            />
                            <div className="flex justify-end gap-3">
                                <Button variant="ghost" onClick={() => setShowCaseNameModal(false)}>Cancel</Button>
                                <Button variant="primary" onClick={handleCaseNameConfirm}>Save</Button>
                            </div>
                        </div>
                    </div>
                )}

                <DebugPanel
                    isOpen={showDebug}
                    onClose={() => setShowDebug(false)}
                    result={null}
                    sessionNonce={sessionNonce}
                    componentName={activeStep.component.type?.name || activeStep.id}
                    goToStep={(stepId) => {
                        const idx = steps.findIndex(s => s.id === stepId);
                        if (idx !== -1) {
                            setCurrentStep(idx);
                            return true;
                        }
                        return false;
                    }}
                />
            </div>
        );
    }

    /* ─────────────────────── Wizard step screen ─────────────────────── */
    return (
        <div className="min-h-screen bg-paper">
            {saveBanner}
            {/* Top chrome — wordmark + save / share */}
            <header className="border-b border-rule bg-white">
                <div className="max-w-[960px] mx-auto px-6 sm:px-8 py-5 flex items-baseline justify-between gap-4">
                    <div>
                        <div className="font-serif text-[18px] text-ink">
                            Estate<span className="italic">clarity</span>
                        </div>
                        <div className="text-[10px] tracking-eyebrow uppercase text-ink3 mt-0.5">
                            Probate &amp; IHT review — in progress
                        </div>
                    </div>
                    <div className="flex items-center gap-4 text-[12px]">
                        <button
                            onClick={handleManualSave}
                            className="inline-flex items-center gap-1.5 text-ink3 hover:text-ink"
                            title="Save your progress"
                        >
                            <Save className="w-3.5 h-3.5" /> Save this audit
                        </button>
                        <button
                            onClick={handleShare}
                            className="inline-flex items-center gap-1.5 text-ink3 hover:text-ink"
                            title="Copy a shareable link to this case"
                        >
                            {shareCopied
                                ? <><CheckCheck className="w-3.5 h-3.5 text-accent" /><span className="text-accent">Link copied</span></>
                                : <><Share2 className="w-3.5 h-3.5" /> Share</>
                            }
                        </button>
                    </div>
                </div>
            </header>

            {/* Debug button */}
            <button
                onClick={() => setShowDebug(true)}
                title="Open Debug Investigation Mode"
                className="fixed top-4 right-4 z-50 inline-flex items-center gap-2 text-[11px] text-ink3 hover:text-ink border border-rule bg-white px-3 py-1.5 transition-colors"
            >
                <AlertTriangle className="w-3.5 h-3.5" /> Debug
            </button>

            <main className="max-w-[720px] mx-auto px-6 sm:px-8 pt-12 pb-20">
                <StepRail
                    steps={railSteps}
                    currentIndex={railIndex}
                    completedIndices={railCompleted}
                    onStepClick={handleStepClick}
                />

                <SectionHeader
                    numeral={['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'XVIII', 'XIX', 'XX'][railIndex]}
                    title={activeStep?.title ?? ''}
                />

                {activeStep?.component}

                {!isLastStep && (
                    <footer className="mt-16 pt-6 border-t border-rule flex items-center justify-between gap-4">
                        <Button variant="ghost" onClick={back} disabled={currentStep === 0}>
                            ← Back
                        </Button>
                        <div className="font-mono text-[11px] text-ink3">
                            {String(currentStep + 1).padStart(2, '0')} / {String(steps.length).padStart(2, '0')}
                        </div>
                        <Button variant="primary" onClick={next} iconTrailing="→" disabled={continueDisabled}>
                            Continue
                        </Button>
                    </footer>
                )}

                {isLastStep && !isAudit && (
                    <footer className="mt-16 pt-6 border-t border-rule flex items-center justify-between gap-4">
                        <Button variant="ghost" onClick={back}>
                            ← Back to edit
                        </Button>
                    </footer>
                )}
            </main>

            {/* Case naming modal */}
            {showCaseNameModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white border border-rule shadow-xl w-full max-w-sm p-6">
                        <h2 className="font-serif text-[20px] text-ink mb-1">Name this case</h2>
                        <p className="text-[12px] text-ink3 mb-4 leading-relaxed">
                            Give this case a name so you can find it again. Leave blank to save without one.
                        </p>
                        <input
                            type="text"
                            autoFocus
                            className="w-full border border-rule bg-white px-3 py-2.5 text-[13px] text-ink focus:border-accent focus:outline-none mb-5"
                            placeholder="e.g. Holloway estate"
                            value={caseNameInput}
                            onChange={e => setCaseNameInput(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') handleCaseNameConfirm();
                                if (e.key === 'Escape') setShowCaseNameModal(false);
                            }}
                        />
                        <div className="flex justify-end gap-3">
                            <Button variant="ghost" onClick={() => setShowCaseNameModal(false)}>Cancel</Button>
                            <Button variant="primary" onClick={handleCaseNameConfirm}>Save</Button>
                        </div>
                    </div>
                </div>
            )}

            <DebugPanel
                isOpen={showDebug}
                onClose={() => setShowDebug(false)}
                result={null}
                sessionNonce={sessionNonce}
                componentName={activeStep.component.type?.name || activeStep.id}
                goToStep={(stepId) => {
                    const idx = steps.findIndex(s => s.id === stepId);
                    if (idx !== -1) {
                        setCurrentStep(idx);
                        return true;
                    }
                    return false;
                }}
            />
        </div>
    );
};
